import { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Snowflake } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function FreezeApproval() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pt-freeze-requests`, { withCredentials: true });
      setRequests(data);
    } catch (error) {
      console.error('Failed to fetch freeze requests:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil data request freeze.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleApprove = async (memberId) => {
    if (!window.confirm('Setujui request freeze ini?')) return;
    setProcessing(memberId);
    try {
      await axios.post(`${API}/pt-freeze-requests/${memberId}/approve`, {}, { withCredentials: true });
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Gagal approve freeze.');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (memberId) => {
    if (!window.confirm('Tolak request freeze ini?')) return;
    setProcessing(memberId);
    try {
      await axios.post(`${API}/pt-freeze-requests/${memberId}/reject`, {}, { withCredentials: true });
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Gagal reject freeze.');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <DashboardLayout>
      <Header
        title="Freeze Approval"
        subtitle="Setujui atau tolak request freeze paket PT dari kasir."
      />
      <div className="px-6 pb-6">
        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-500">Memuat data...</div>
        ) : requests.length === 0 ? (
          <div className="border border-white/10 bg-zinc-950/40 py-10 text-center">
            <Snowflake className="mx-auto mb-3 h-8 w-8 text-zinc-600" strokeWidth={1.5} />
            <p className="text-sm text-zinc-500">Belum ada request freeze.</p>
          </div>
        ) : (
          <div className="overflow-hidden border border-white/10 bg-zinc-950/40">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.025]">
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Nama Member</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Merchant</th>
                    <th className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Hari</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Plan</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Tanggal Request</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Oleh</th>
                    <th className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req) => (
                    <tr key={req.member_id} className="border-b border-white/5 last:border-0">
                      <td className="px-4 py-3 font-medium text-white">{req.member_name || '-'}</td>
                      <td className="px-4 py-3 text-zinc-400">{req.merchant_name || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="outline" className="rounded-sm text-xs bg-[#D4AF37]/10 text-[#F3D77A] border-[#D4AF37]/30">
                          {req.freeze_pending?.requested_days || 0} hari
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-zinc-400">{req.plan_type || '-'}</td>
                      <td className="px-4 py-3 text-zinc-400">{formatDate(req.freeze_pending?.requested_at)}</td>
                      <td className="px-4 py-3 text-zinc-400">{req.freeze_pending?.requested_by || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(req.member_id)}
                            disabled={processing === req.member_id}
                            className="h-8 gap-1 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
                          >
                            <CheckCircle className="h-3.5 w-3.5" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(req.member_id)}
                            disabled={processing === req.member_id}
                            className="h-8 gap-1 rounded-sm border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <XCircle className="h-3.5 w-3.5" /> Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
